'use client';
import React, { ReactNode, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { ClickButton } from './click-button';
import { Button } from './ui/button';

const ASK_DIALOG_VIEWPORT_ID = 'ask-dialog-viewport';

type AskDialogProps = {
  title: string;
  body: ReactNode;
  confirmText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: (confirmed: boolean) => void;
};

export type AskWithDialogArgs = Pick<AskDialogProps, 'title' | 'body' | 'confirmText'>;

export function askWithDialog({ onClose, ...props }: AskDialogProps) {
  const viewport = document.getElementById(ASK_DIALOG_VIEWPORT_ID);

  if (!viewport) {
    throw new Error('ask dialog viewport not found');
  } else {
    return new Promise((resolve) => {
      const root = createRoot(viewport);

      root.render(
        <AskDialog
          {...props}
          onClose={(confirmed) => {
            onClose?.(confirmed);

            root.unmount();

            resolve(confirmed);
          }}
        />
      );
    });
  }
}

function AskDialog({ title, body, confirmText = 'Confirm', onConfirm, onCancel, onClose }: AskDialogProps) {
  const [open, setOpen] = useState(true);

  function cancel() {
    onCancel?.();
    onClose?.(false);
    setOpen(false);
  }

  async function confirm() {
    await onConfirm?.();
    onClose?.(true);
    setOpen(false);
  }

  return (
    <Dialog onOpenChange={(open) => !open && cancel()} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {body}
          <div className='flex flex-row-reverse gap-4 pt-4'>
            <ClickButton clickToDisable onClick={confirm}>
              <div className='overflow-hidden text-ellipsis max-w-[15rem]'>{confirmText}</div>
            </ClickButton>
            <Button onClick={cancel} variant='outline'>
              Cancel
            </Button>
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export function AskDialogViewport() {
  return <div id={ASK_DIALOG_VIEWPORT_ID} />;
}
